import type { PosterThemeConfig } from '@runtime/core/theme/themeEngineShared'
import { getPosterThemeAccessToken } from '@runtime/core/theme/posterAccess'
import { resolveApiUrl } from '@runtime/shared/api/fetchJSON'

function appendThemeAccessQuery(url: string, accessToken?: string | null) {
  if (!accessToken) return url
  const separator = url.includes('?') ? '&' : '?'
  return `${url}${separator}access=${encodeURIComponent(accessToken)}`
}

const THEME_JSON_FILES = (id: string) => {
  const accessToken = getPosterThemeAccessToken(id)
  const urls = [resolveApiUrl(`/api/poster-themes/${id}/config`)]

  if (accessToken) {
    urls.unshift(
      resolveApiUrl(
        appendThemeAccessQuery(`/api/poster-themes/${id}/config`, accessToken),
      ),
    )
  }

  return urls
}

export function isOffline() {
  try {
    return typeof navigator !== 'undefined' && navigator.onLine === false
  } catch {
    return false
  }
}

function toThemeAssetUrl(themeId: string, value: string | null | undefined) {
  if (!value) return null
  const raw = String(value)
  if (/^(https?:|data:|blob:)/i.test(raw)) return null
  if (raw.startsWith('/')) return resolveApiUrl(raw)
  if (/^assets\//i.test(raw)) return '/' + raw
  return resolveApiUrl(`/api/poster-themes/${themeId}/preview/${raw}`)
}

async function readThemeConfigFromCache(themeId: string) {
  if (typeof caches === 'undefined') return null
  for (const url of THEME_JSON_FILES(themeId)) {
    try {
      const res =
        (await caches.match(url)) ||
        (url.startsWith('/') ? await caches.match(url.slice(1)) : undefined)
      if (!res) continue
      return (await res.clone().json()) as PosterThemeConfig
    } catch {
      // ignore
    }
  }
  return null
}

export async function hasCachedThemeAssets(themeId: string) {
  if (typeof caches === 'undefined') return false
  const cfg = await readThemeConfigFromCache(themeId)
  if (!cfg) return false

  const urls: string[] = []
  if (cfg.preview) {
    const url = toThemeAssetUrl(themeId, cfg.preview)
    if (url) urls.push(url)
  }
  if (cfg.tiles) {
    const center = toThemeAssetUrl(themeId, cfg.tiles.center)
    if (center) urls.push(center)
    if (Array.isArray(cfg.tiles.set)) {
      cfg.tiles.set.forEach((tile) => {
        const url = toThemeAssetUrl(themeId, tile)
        if (url) urls.push(url)
      })
    }
  }
  if (cfg.homePreviews && typeof cfg.homePreviews === 'object') {
    Object.values(cfg.homePreviews).forEach((val) => {
      const url = toThemeAssetUrl(themeId, val)
      if (url) urls.push(url)
    })
  }

  const unique = Array.from(new Set(urls))
  if (unique.length === 0) return true

  for (const url of unique) {
    try {
      const res =
        (await caches.match(url)) ||
        (url.startsWith('/') ? await caches.match(url.slice(1)) : undefined)
      if (!res) return false
    } catch {
      return false
    }
  }
  return true
}
